import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { FaSave } from "react-icons/fa";
import PieChats from "../../graphs/PieChats";
import { GRAPH_OPTIONS, TABLE_OPTIONS, PERSONA_BAR_PALETTE } from "../constants";
import { GroupedBarTooltip } from "./shared";
import uiConfig from "../../../config/uiConfig";

const CustomizerDrawer = ({
  showCustomizer,
  setShowCustomizer,
  customizerWidth,
  setCustomizerWidth,
  selectedKpiSlots,
  setActiveKpiSlot,
  clearKpiSlot,
  showKpiPicker,
  setShowKpiPicker,
  activeKpiSlot,
  kpiCards,
  usedKpiIds,
  selectKpiForSlot,
  showLayoutPicker,
  setShowLayoutPicker,
  addCustomLayout,
  customLayouts,
  removeCustomLayout,
  activeSlotPicker,
  setActiveSlotPicker,
  selectSlotItem,
  clearSlotItem,
  updateSlotTitle,
  usedGraphIds,
  usedTableIds,
  previewKpiGridClass,
  charts,
  tables,
  reportsByPersonaData,
  personaNames,
  pieKeys,
  abbreviateReportName,
  configMessage,
  savingConfig,
  saveConfig,
}) => {
  if (!showCustomizer) return null;

  return (
    <div className="fixed inset-0 z-[1200] !m-0 flex justify-end" role="dialog" aria-modal="true" aria-label="Customize analytics">
      <button type="button" aria-label="Close customization" className="absolute inset-0 bg-slate-950/35 backdrop-blur-[2px]" onClick={() => setShowCustomizer(false)} />
      <aside className={`relative z-10 flex h-full w-full flex-col bg-slate-50 shadow-2xl ring-1 ring-black/10 ${customizerWidth === "narrow" ? "sm:w-1/3" : customizerWidth === "wide" ? "sm:w-[95%]" : customizerWidth === "full" ? "sm:w-full" : "sm:w-1/2"}`}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--app-button-color)]">Dashboard studio</p>
            <h2 className="mt-1 text-lg font-bold text-slate-900">Customize analytics</h2>
            <p className="mt-0.5 text-xs text-slate-500">Shape the dashboard before publishing it.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-1 rounded-lg bg-slate-100 p-1 sm:flex" aria-label="Customizer width">
              {[["narrow", "Narrow"], ["standard", "Half screen"], ["wide", "Wide 95%"], ["full", "Full screen"]].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCustomizerWidth(value)}
                  className={`rounded-md px-2 py-1 text-[10px] font-semibold transition ${customizerWidth === value ? "bg-white shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                  style={customizerWidth === value ? { color: "var(--app-button-color)" } : undefined}
                >
                  {label}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setShowCustomizer(false)} className="rounded-lg p-2 text-xl leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close">×</button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="mb-5 rounded-2xl border border-slate-200 bg-white shadow-sm" style={{ boxShadow: "inset 0 3px 0 var(--app-button-color)" }}>
            <div className="bg-slate-50/70 p-4 relative">
              {/* KPI Slots */}
              <KpiSlotsSection
                selectedKpiSlots={selectedKpiSlots}
                kpiCards={kpiCards}
                clearKpiSlot={clearKpiSlot}
                setActiveKpiSlot={setActiveKpiSlot}
                setShowKpiPicker={setShowKpiPicker}
                previewKpiGridClass={previewKpiGridClass}
              />

              {/* KPI Picker Popup */}
              {showKpiPicker && (
                <KpiPickerPopup
                  activeKpiSlot={activeKpiSlot}
                  kpiCards={kpiCards}
                  usedKpiIds={usedKpiIds}
                  selectKpiForSlot={selectKpiForSlot}
                  onClose={() => { setShowKpiPicker(false); setActiveKpiSlot(null); }}
                />
              )}

              {/* Add Layout Button */}
              <div className="mb-3 relative">
                <button
                  type="button"
                  onClick={() => setShowLayoutPicker((open) => !open)}
                  className="flex items-center gap-2 rounded-md px-3 py-1.5 text-[11px] font-semibold text-white"
                  style={{ background: "var(--app-button-color)" }}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Layout
                </button>
                {showLayoutPicker && (
                  <LayoutPickerPopup
                    addCustomLayout={addCustomLayout}
                    onClose={() => setShowLayoutPicker(false)}
                  />
                )}
              </div>

              {/* Custom Layout Sections */}
              {customLayouts.map((layout) => (
                <CustomLayoutSection
                  key={layout.id}
                  layout={layout}
                  removeCustomLayout={removeCustomLayout}
                  activeSlotPicker={activeSlotPicker}
                  setActiveSlotPicker={setActiveSlotPicker}
                  selectSlotItem={selectSlotItem}
                  clearSlotItem={clearSlotItem}
                  updateSlotTitle={updateSlotTitle}
                  usedGraphIds={usedGraphIds}
                  usedTableIds={usedTableIds}
                  charts={charts}
                  tables={tables}
                  reportsByPersonaData={reportsByPersonaData}
                  personaNames={personaNames}
                  pieKeys={pieKeys}
                  abbreviateReportName={abbreviateReportName}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-5 py-4">
          <span className="max-w-[220px] text-xs text-slate-500">{configMessage}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowCustomizer(false)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveConfig}
              disabled={savingConfig}
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
              style={{ background: "var(--app-button-color)" }}
            >
              <FaSave size={11} />
              {savingConfig ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
};

// KPI Slots Section
const KpiSlotsSection = ({
  selectedKpiSlots,
  kpiCards,
  clearKpiSlot,
  setActiveKpiSlot,
  setShowKpiPicker,
  previewKpiGridClass,
}) => (
  <div className={`mb-3 grid ${previewKpiGridClass} gap-3`}>
    {[0, 1, 2, 3].map((slotIndex) => {
      const kpiId = selectedKpiSlots[slotIndex];
      const kpi = kpiId ? kpiCards.find((k) => k.id === kpiId) : null;

      if (kpi) {
        return (
          <div key={slotIndex} className="relative rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <button
              type="button"
              onClick={() => clearKpiSlot(slotIndex)}
              className="absolute right-2 top-2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title="Remove KPI"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex items-center gap-3">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-lg"
                style={{ background: "color-mix(in srgb, var(--app-button-color) 12%, white)", color: "var(--app-button-color)" }}
              >
                {kpi.icon}
              </div>
              <div>
                <p className="text-lg font-bold text-slate-800">{kpi.value ?? "—"}</p>
                <p className="text-[11px] font-medium text-slate-500">{kpi.label}</p>
              </div>
            </div>
            {kpi.trend && kpi.trend.value !== null && (
              <div className={`mt-2 flex items-center gap-1 text-[10px] font-medium ${kpi.trend.up ? "text-emerald-600" : "text-rose-500"}`}>
                <span>{kpi.trend.up ? "↑" : "↓"} {kpi.trend.value}%</span>
                <span className="text-slate-400">{kpi.trend.label}</span>
              </div>
            )}
          </div>
        );
      }

      return (
        <button
          key={slotIndex}
          type="button"
          onClick={() => { setActiveKpiSlot(slotIndex); setShowKpiPicker(true); }}
          className="group flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-white p-4 transition hover:border-[var(--app-button-color)] hover:bg-slate-50"
        >
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{ background: "color-mix(in srgb, var(--app-button-color) 12%, white)" }}
          >
            <svg className="h-5 w-5" style={{ color: "var(--app-button-color)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <span className="text-[11px] font-medium text-slate-500 group-hover:text-slate-700">Add KPI</span>
        </button>
      );
    })}
  </div>
);

// KPI Picker Popup
const KpiPickerPopup = ({ activeKpiSlot, kpiCards, usedKpiIds, selectKpiForSlot, onClose }) => (
  <div className="absolute left-1/2 top-4 z-30 w-72 -translate-x-1/2 rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
    <div className="mb-3 flex items-center justify-between">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-600">Select KPI</p>
        <p className="mt-0.5 text-[10px] text-slate-400">Choose one for slot {(activeKpiSlot ?? 0) + 1}</p>
      </div>
      <button type="button" onClick={onClose} className="rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100">×</button>
    </div>
    <div className="max-h-64 space-y-1.5 overflow-auto pr-1">
      {kpiCards.map((kpi) => {
        const isUsed = usedKpiIds.includes(kpi.id);
        return (
          <button
            key={kpi.id}
            type="button"
            onClick={() => !isUsed && selectKpiForSlot(kpi.id)}
            disabled={isUsed}
            className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-xs transition ${
              isUsed
                ? "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed"
                : "border-slate-200 text-slate-700 hover:border-[var(--app-button-color)] hover:bg-slate-50"
            }`}
          >
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg"
              style={{ background: isUsed ? "#f1f5f9" : "color-mix(in srgb, var(--app-button-color) 12%, white)", color: isUsed ? "#94a3b8" : "var(--app-button-color)" }}
            >
              {kpi.icon}
            </div>
            <div className="flex-1">
              <span className={`font-medium ${isUsed ? "text-slate-400" : "text-slate-700"}`}>{kpi.label}</span>
            </div>
            {isUsed && <span className="text-[9px] text-slate-400">In use</span>}
          </button>
        );
      })}
    </div>
  </div>
);

// Layout Picker Popup
const LayoutPickerPopup = ({ addCustomLayout, onClose }) => (
  <div className="absolute left-0 top-10 z-20 w-48 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
    <div className="mb-2 flex items-center justify-between">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-600">Select Layout</p>
      <button type="button" onClick={onClose} className="rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100">×</button>
    </div>
    <div className="space-y-1">
      {[[1, "1 in row"], [2, "2 in row"], [3, "3 in row"]].map(([cols, label]) => (
        <button
          key={cols}
          type="button"
          onClick={() => addCustomLayout(cols)}
          className="flex w-full items-center gap-3 rounded-md border border-slate-200 px-3 py-2 text-left text-xs transition hover:border-[var(--app-button-color)] hover:bg-slate-50"
        >
          <div className="flex gap-1">
            {Array.from({ length: cols }).map((_, i) => (
              <div key={i} className="h-4 w-4 rounded bg-slate-200" />
            ))}
          </div>
          <span className="text-slate-700">{label}</span>
        </button>
      ))}
    </div>
  </div>
);

// Custom Layout Section
const CustomLayoutSection = ({
  layout,
  removeCustomLayout,
  activeSlotPicker,
  setActiveSlotPicker,
  selectSlotItem,
  clearSlotItem,
  updateSlotTitle,
  usedGraphIds,
  usedTableIds,
  charts,
  tables,
  reportsByPersonaData,
  personaNames,
  pieKeys,
  abbreviateReportName,
}) => (
  <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
    <div className="mb-2 flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Section · {layout.columns} in row</p>
      <button
        type="button"
        onClick={() => removeCustomLayout(layout.id)}
        className="rounded px-1.5 py-0.5 text-[10px] font-medium text-rose-500 hover:bg-rose-50"
      >
        Remove
      </button>
    </div>
    <div
      className={`grid gap-3 ${layout.columns === 1 ? "grid-cols-1" : layout.columns === 2 ? "grid-cols-2" : "grid-cols-3"}`}
      style={{ height: 400 }}
    >
      {Array.from({ length: layout.columns }).map((_, index) => (
        <SlotCell
          key={index}
          layout={layout}
          index={index}
          activeSlotPicker={activeSlotPicker}
          setActiveSlotPicker={setActiveSlotPicker}
          selectSlotItem={selectSlotItem}
          clearSlotItem={clearSlotItem}
          updateSlotTitle={updateSlotTitle}
          usedGraphIds={usedGraphIds}
          usedTableIds={usedTableIds}
          charts={charts}
          tables={tables}
          reportsByPersonaData={reportsByPersonaData}
          personaNames={personaNames}
          pieKeys={pieKeys}
          abbreviateReportName={abbreviateReportName}
        />
      ))}
    </div>
  </div>
);

// Slot Cell
const SlotCell = ({
  layout,
  index,
  activeSlotPicker,
  setActiveSlotPicker,
  selectSlotItem,
  clearSlotItem,
  updateSlotTitle,
  usedGraphIds,
  usedTableIds,
  charts,
  tables,
  reportsByPersonaData,
  personaNames,
  pieKeys,
  abbreviateReportName,
}) => {
  const slotItem = layout.slots?.[index];
  const isGraphPickerOpen = activeSlotPicker?.layoutId === layout.id && activeSlotPicker?.slotIndex === index && activeSlotPicker?.type === "graph";
  const isTablePickerOpen = activeSlotPicker?.layoutId === layout.id && activeSlotPicker?.slotIndex === index && activeSlotPicker?.type === "table";

  return (
    <div className="relative flex flex-col rounded-xl border border-slate-200 bg-white overflow-hidden" style={{ height: "100%" }}>
      {slotItem ? (
        <FilledSlot
          slotItem={slotItem}
          layout={layout}
          index={index}
          clearSlotItem={clearSlotItem}
          updateSlotTitle={updateSlotTitle}
          charts={charts}
          tables={tables}
          reportsByPersonaData={reportsByPersonaData}
          personaNames={personaNames}
          pieKeys={pieKeys}
          abbreviateReportName={abbreviateReportName}
        />
      ) : (
        <EmptySlot
          layout={layout}
          index={index}
          setActiveSlotPicker={setActiveSlotPicker}
          isGraphPickerOpen={isGraphPickerOpen}
          isTablePickerOpen={isTablePickerOpen}
          selectSlotItem={selectSlotItem}
          usedGraphIds={usedGraphIds}
          usedTableIds={usedTableIds}
        />
      )}
    </div>
  );
};

// Filled Slot Content
const FilledSlot = ({
  slotItem,
  layout,
  index,
  clearSlotItem,
  updateSlotTitle,
  charts,
  tables,
  reportsByPersonaData,
  personaNames,
  pieKeys,
  abbreviateReportName,
}) => {
  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [editedTitle, setEditedTitle] = React.useState(slotItem.title || "");
  const inputRef = React.useRef(null);

  const defaultLabel = slotItem.type === "graph"
    ? GRAPH_OPTIONS.find((g) => g.id === slotItem.id)?.label
    : TABLE_OPTIONS.find((t) => t.id === slotItem.id)?.label;

  const displayTitle = slotItem.title || defaultLabel;

  const handleSaveTitle = () => {
    if (editedTitle.trim()) {
      updateSlotTitle(layout.id, index, editedTitle.trim());
    }
    setIsEditingTitle(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveTitle();
    } else if (e.key === "Escape") {
      setEditedTitle(displayTitle);
      setIsEditingTitle(false);
    }
  };

  React.useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingTitle]);

  return (
    <div className="flex flex-col h-full w-full">
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-slate-100"
        style={{ background: "color-mix(in srgb, var(--app-button-color) 5%, white)" }}
      >
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-wide" style={{ color: "var(--app-button-color)" }}>{slotItem.type}</p>
          {isEditingTitle ? (
            <input
              ref={inputRef}
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={handleSaveTitle}
              onKeyDown={handleKeyDown}
              className="w-full text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded px-1.5 py-0.5 outline-none focus:border-[var(--app-button-color)] focus:ring-1 focus:ring-[var(--app-button-color)]"
            />
          ) : (
            <span className="inline-flex items-center gap-1">
              <span className="text-xs font-semibold text-slate-700">{displayTitle}</span>
              <button
                type="button"
                onClick={() => { setEditedTitle(displayTitle); setIsEditingTitle(true); }}
                className="inline-flex items-center justify-center w-4 h-4 rounded hover:bg-slate-200"
                title="Edit title"
              >
                <svg className="w-3 h-3 text-slate-400 hover:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => clearSlotItem(layout.id, index)}
          className="text-[10px] font-medium text-slate-500 hover:text-rose-500 px-2 py-1 rounded hover:bg-slate-100 flex-shrink-0"
        >
          Change
        </button>
      </div>
      <div className="flex-1 p-2 overflow-hidden">
        <SlotItemContent
          slotItem={slotItem}
          charts={charts}
          tables={tables}
          reportsByPersonaData={reportsByPersonaData}
          personaNames={personaNames}
          pieKeys={pieKeys}
          abbreviateReportName={abbreviateReportName}
        />
      </div>
    </div>
  );
};

// Empty Slot with Graph/Table picker options
const EmptySlot = ({
  layout,
  index,
  setActiveSlotPicker,
  isGraphPickerOpen,
  isTablePickerOpen,
  selectSlotItem,
  usedGraphIds,
  usedTableIds,
}) => (
  <div className="flex flex-col items-center justify-center gap-4 h-full rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-4">
    <p className="text-[10px] text-slate-400 text-center">Please select and continue adding graphs and tables</p>
    <div className="flex w-full items-center justify-center gap-4">
      {/* Graph option */}
      <button
        type="button"
        onClick={() => setActiveSlotPicker({ layoutId: layout.id, slotIndex: index, type: "graph" })}
        className="group flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-white p-3 transition hover:border-[var(--app-button-color)] hover:shadow-md"
      >
        <div className="flex h-16 items-end gap-1">
          {[40, 65, 45, 80, 55, 70].map((h, i) => (
            <div key={i} className="w-2 rounded-t transition-all group-hover:scale-110" style={{ height: `${h}%`, background: "var(--app-button-color)" }} />
          ))}
        </div>
        <span className="text-[10px] font-semibold text-slate-600 group-hover:text-slate-800">Graph</span>
      </button>
      {/* Table option */}
      <button
        type="button"
        onClick={() => setActiveSlotPicker({ layoutId: layout.id, slotIndex: index, type: "table" })}
        className="group flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-white p-3 transition hover:border-[var(--app-button-color)] hover:shadow-md"
      >
        <div className="w-24 rounded border border-slate-200 bg-white overflow-hidden">
          <div className="flex border-b border-slate-200 text-[6px] font-semibold text-slate-500" style={{ background: "color-mix(in srgb, var(--app-button-color) 8%, white)" }}>
            <div className="flex-1 px-1 py-0.5 border-r border-slate-200">Name</div>
            <div className="w-6 px-1 py-0.5 text-right">Val</div>
          </div>
          {["A", "B", "C"].map((item, i) => (
            <div key={i} className={`flex text-[6px] text-slate-500 ${i < 2 ? "border-b border-slate-100" : ""}`}>
              <div className="flex-1 px-1 py-0.5 border-r border-slate-100">Item {item}</div>
              <div className="w-6 px-1 py-0.5 text-right font-medium">{[84, 72, 58][i]}</div>
            </div>
          ))}
        </div>
        <span className="text-[10px] font-semibold text-slate-600 group-hover:text-slate-800">Table</span>
      </button>
    </div>

    {/* Graph picker popup */}
    {isGraphPickerOpen && (
      <ItemPickerPopup
        type="Graph"
        items={GRAPH_OPTIONS}
        usedIds={usedGraphIds}
        onSelect={(id) => selectSlotItem(layout.id, index, id, "graph")}
        onClose={() => setActiveSlotPicker(null)}
      />
    )}

    {/* Table picker popup */}
    {isTablePickerOpen && (
      <ItemPickerPopup
        type="Table"
        items={TABLE_OPTIONS}
        usedIds={usedTableIds}
        onSelect={(id) => selectSlotItem(layout.id, index, id, "table")}
        onClose={() => setActiveSlotPicker(null)}
      />
    )}
  </div>
);

// Item Picker Popup (for graphs and tables)
const ItemPickerPopup = ({ type, items, usedIds, onSelect, onClose }) => (
  <div className="absolute left-1/2 top-1/2 z-30 w-52 -translate-x-1/2 -translate-y-1/2 rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
    <div className="mb-2 flex items-center justify-between">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-600">Select {type}</p>
      <button type="button" onClick={onClose} className="rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100">×</button>
    </div>
    <div className="space-y-1">
      {items.map((item) => {
        const isUsed = usedIds.includes(item.id);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => !isUsed && onSelect(item.id)}
            disabled={isUsed}
            className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-xs transition ${
              isUsed
                ? "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed"
                : "border-slate-200 text-slate-700 hover:border-[var(--app-button-color)] hover:bg-slate-50"
            }`}
          >
            <svg className={`h-4 w-4 ${isUsed ? "text-slate-300" : "text-slate-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              {type === "Graph" ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              )}
            </svg>
            <span>{item.label}</span>
            {isUsed && <span className="ml-auto text-[9px] text-slate-400">In use</span>}
          </button>
        );
      })}
    </div>
  </div>
);

// Slot Item Content (renders the actual chart/table in the customizer preview)
const SlotItemContent = ({
  slotItem,
  charts,
  tables,
  reportsByPersonaData,
  personaNames,
  pieKeys,
  abbreviateReportName,
}) => {
  const NoData = () => <div className="h-full flex items-center justify-center text-xs text-slate-400">No data available</div>;

  // Graph content
  if (slotItem.type === "graph") {
    switch (slotItem.id) {
      case "viewsByPersona":
        return reportsByPersonaData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={reportsByPersonaData} margin={{ top: 4, right: 10, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="reportName" tick={{ fontSize: 9, fill: "#9ca3af" }} tickLine={false} interval={0} tickFormatter={abbreviateReportName} />
              <YAxis tick={{ fontSize: 9, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <Tooltip content={<GroupedBarTooltip />} cursor={{ fill: "#f3f4f6" }} />
              <Legend wrapperStyle={{ fontSize: "9px" }} />
              {personaNames.map((persona, i) => (
                <Bar key={persona} dataKey={persona} name={persona} fill={PERSONA_BAR_PALETTE[i % PERSONA_BAR_PALETTE.length]} radius={[2, 2, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : <NoData />;

      case "dailyActivity":
        return charts.byDate?.dates?.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.byDate.dates.map((d, i) => ({ date: d, views: charts.byDate.views[i] ?? 0 }))} margin={{ top: 4, right: 10, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#9ca3af" }} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: "10px", borderRadius: "6px" }} />
              <Bar dataKey="views" fill={uiConfig.chartPrimaryColor || "#3b82f6"} radius={[2, 2, 0, 0]} name="Views" />
            </BarChart>
          </ResponsiveContainer>
        ) : <NoData />;

      case "reportDistribution":
        return pieKeys.length > 0 ? (
          <PieChats data={charts.byGroup} selectedCategory={pieKeys[0]} showLegend={true} chartType="donut" />
        ) : <NoData />;

      case "topReportsGraph":
        return tables.topReports.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={tables.topReports.slice(0, 6).map((row) => ({ name: row.reportName, views: row.count }))} layout="vertical" margin={{ top: 4, right: 10, left: 5, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fontSize: 9, fill: "#9ca3af" }} />
              <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 8, fill: "#6b7280" }} />
              <Tooltip />
              <Bar dataKey="views" fill="var(--app-button-color)" radius={[0, 2, 2, 0]} name="Views" />
            </BarChart>
          </ResponsiveContainer>
        ) : <NoData />;

      case "userActivityGraph":
        return tables.userActivity.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={tables.userActivity.slice(0, 6).map((row) => ({ name: row.email, views: row.views }))} layout="vertical" margin={{ top: 4, right: 10, left: 5, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fontSize: 9, fill: "#9ca3af" }} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 7, fill: "#6b7280" }} />
              <Tooltip />
              <Bar dataKey="views" fill="var(--app-icon-color)" radius={[0, 2, 2, 0]} name="Views" />
            </BarChart>
          </ResponsiveContainer>
        ) : <NoData />;

      default:
        return <NoData />;
    }
  }

  // Table content
  if (slotItem.type === "table") {
    const tableClass = "w-full text-[10px]";
    const headClass = "sticky top-0 bg-slate-50";
    const thClass = "px-2 py-1.5 text-left font-semibold text-slate-600";
    const tdClass = "px-2 py-1.5";

    switch (slotItem.id) {
      case "topReports":
        return (
          <div className="h-full overflow-auto">
            <table className={tableClass}>
              <thead className={headClass}>
                <tr className="border-b border-slate-200">
                  <th className={thClass}>#</th>
                  <th className={thClass}>Report</th>
                  <th className={thClass}>Group</th>
                  <th className={`${thClass} text-right`}>Views</th>
                </tr>
              </thead>
              <tbody>
                {tables.topReports.slice(0, 8).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className={`${tdClass} text-slate-400`}>{i + 1}</td>
                    <td className={`${tdClass} text-slate-700 truncate max-w-[100px]`} title={r.reportName}>{r.reportName}</td>
                    <td className={tdClass}><span className="bg-indigo-50 text-indigo-600 text-[8px] font-semibold px-1.5 py-0.5 rounded-full">{r.userGroupName}</span></td>
                    <td className={`${tdClass} text-right font-medium text-slate-700`}>{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tables.topReports.length === 0 && <NoData />}
          </div>
        );

      case "topVisuals":
        return (
          <div className="h-full overflow-auto">
            <table className={tableClass}>
              <thead className={headClass}>
                <tr className="border-b border-slate-200">
                  <th className={thClass}>#</th>
                  <th className={thClass}>Visual</th>
                  <th className={thClass}>Group</th>
                  <th className={`${thClass} text-right`}>Uses</th>
                </tr>
              </thead>
              <tbody>
                {tables.topVisuals.slice(0, 8).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className={`${tdClass} text-slate-400`}>{i + 1}</td>
                    <td className={`${tdClass} text-slate-700 truncate max-w-[100px]`} title={r.title}>{r.title}</td>
                    <td className={tdClass}><span className="bg-emerald-50 text-emerald-600 text-[8px] font-semibold px-1.5 py-0.5 rounded-full">{r.userGroupName}</span></td>
                    <td className={`${tdClass} text-right font-medium text-slate-700`}>{r.saveCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tables.topVisuals.length === 0 && <NoData />}
          </div>
        );

      case "userActivity":
        return (
          <div className="h-full overflow-auto">
            <table className={tableClass}>
              <thead className={headClass}>
                <tr className="border-b border-slate-200">
                  <th className={thClass}>#</th>
                  <th className={thClass}>Email</th>
                  <th className={`${thClass} text-right`}>Views</th>
                </tr>
              </thead>
              <tbody>
                {tables.userActivity.slice(0, 8).map((u, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className={`${tdClass} text-slate-400`}>{i + 1}</td>
                    <td className={`${tdClass} text-slate-700 truncate max-w-[120px]`} title={u.email}>{u.email}</td>
                    <td className={`${tdClass} text-right font-medium text-slate-700`}>{u.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tables.userActivity.length === 0 && <NoData />}
          </div>
        );

      case "inactiveUsers":
        return (
          <div className="h-full overflow-auto">
            <table className={tableClass}>
              <thead className={headClass}>
                <tr className="border-b border-slate-200">
                  <th className={thClass}>#</th>
                  <th className={thClass}>Email</th>
                  <th className={thClass}>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {tables.inactiveUsers.slice(0, 8).map((u, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className={`${tdClass} text-slate-400`}>{i + 1}</td>
                    <td className={`${tdClass} text-slate-700 truncate max-w-[120px]`} title={u.email}>{u.email}</td>
                    <td className={`${tdClass} text-slate-500`}>{u.lastActivity ? u.lastActivity.slice(0, 10) : <span className="text-rose-500">Never</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tables.inactiveUsers.length === 0 && <NoData />}
          </div>
        );

      case "unusedReports":
        return (
          <div className="h-full overflow-auto">
            <table className={tableClass}>
              <thead className={headClass}>
                <tr className="border-b border-slate-200">
                  <th className={thClass}>#</th>
                  <th className={thClass}>Report</th>
                  <th className={thClass}>Group</th>
                </tr>
              </thead>
              <tbody>
                {tables.unusedReports.slice(0, 8).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className={`${tdClass} text-slate-400`}>{i + 1}</td>
                    <td className={`${tdClass} text-slate-700 truncate max-w-[100px]`} title={r.reportName}>{r.reportName}</td>
                    <td className={tdClass}><span className="bg-indigo-50 text-indigo-600 text-[8px] font-semibold px-1.5 py-0.5 rounded-full">{r.userGroupName}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tables.unusedReports.length === 0 && <NoData />}
          </div>
        );

      default:
        return <NoData />;
    }
  }

  return <NoData />;
};

export default CustomizerDrawer;
